import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";

const AddMoney = () => {
  const [selectedTannits, setSelectedTannits] = useState(null);
  const [customTannits, setCustomTannits] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Paypal");
  const [isProcessing, setIsProcessing] = useState(false);
  const [reference, setReference] = useState("");
  const [paymentType, setPaymentType] = useState("one-time"); // 'one-time' or 'subscription'
  const [selectedPlan, setSelectedPlan] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  const user = useSelector((state) => state.auth.user);
  const { id: userId } = user.claims;
  const navigate = useNavigate();

  // Subscription plans data
  const subscriptionPlans = [
    {
      id: "classic",
      name: "Classic (Free)",
      price: 0,
      interval: "month",
      tannits: 0,
      features: [
        "Limited EcoVest features",
        "Standard fees",
        "No secondary market"
      ]
    },
    {
      id: "ecovest",
      name: "EcoVest Unlimited",
      price: 40,
      interval: "month",
      tannits: 400,
      features: [
        "Full EcoVest gamification",
        "reduced fees",
        "No secondary market"
      ],
      popular: true
    },
    {
      id: "pro",
      name: "Pro Investor",
      price: 400,
      interval: "month",
      tannits: 4000,
      features: [
        "Complete EcoVest experience",
        "lowest fees",
        "Secondary market",
        "Advanced analytics and reports"
      ]
    }
  ];

  const handleRadioChange = (value) => {
    setSelectedTannits(value);
    setCustomTannits("");
    setSelectedPlan(null); // Clear selected plan when choosing one-time option
  };

  const handleCustomChange = (e) => {
    setSelectedTannits(null);
    setCustomTannits(e.target.value);
    setSelectedPlan(null); // Clear selected plan when choosing custom amount
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setSelectedTannits(null);
    setCustomTannits("");
    setPaymentType("subscription");
  };

  const handlePaymentTypeChange = (type) => {
    setPaymentType(type);
    setSelectedPlan(null);
    setSelectedTannits(null);
    setCustomTannits("");
  };

  const handleReferenceChange = (e) => {
    setReference(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (paymentType === "one-time") {
      // Existing one-time payment logic
      const tannitsTopped = selectedTannits || customTannits;

      if (!tannitsTopped || tannitsTopped <= 0) {
        alert("Please enter a valid amount of Tannits");
        return;
      }

      if (paymentMethod === "Investment Broker" && !reference) {
        alert("Please enter a reference for Investment Broker payment");
        return;
      }

      const transaction = {
        TannitsTopped: parseInt(tannitsTopped, 10),
        Price: parseFloat(tannitsTopped) * 10,
        CurrencyId: 1,
        Description: "Top-up transaction",
        InvestorId: userId,
        CreatedAt: new Date().toISOString(),
        Vested: false,
      };
      sessionStorage.setItem('currentTransaction', JSON.stringify(transaction));
      setIsProcessing(true);

      try {
        if (paymentMethod === "Stripe") {
          const response = await axios.post(
            `${apiUrl}/payment/create-stripe-session`,
            {
              price: transaction.Price,
              description: transaction.Description,
            }
          );
          const { id } = response.data;
          const stripe = await loadStripe("pk_test_51RB5LYPBzcrWLWYLheaSSVZeMr5PMfi8G3Tro3qAIRejyop9UyBHBkwQ0vOoSBUuYQ3tBlwxlER9xLB2rmVQM9tZ00h8h5YOME");
          await stripe.redirectToCheckout({ sessionId: id });
        }
        // ... rest of your one-time payment logic
      } catch (error) {
        // ... error handling
      }
    } else {
      // Subscription payment logic
      if (!selectedPlan) {
        alert("Please select a subscription plan");
        return;
      }

      setIsProcessing(true);
      try {
        if (paymentMethod === "Stripe") {
          const response = await axios.post(
            `${apiUrl}/payment/create-stripe-subscription`,
            {
              planId: selectedPlan.id,
              customerId: userId,
              tannits: selectedPlan.tannits,
              price: selectedPlan.price
            }
          );
          const { sessionId } = response.data;
          const stripe = await loadStripe("pk_test_51RB5LYPBzcrWLWYLheaSSVZeMr5PMfi8G3Tro3qAIRejyop9UyBHBkwQ0vOoSBUuYQ3tBlwxlER9xLB2rmVQM9tZ00h8h5YOME");
          await stripe.redirectToCheckout({ sessionId });
        }
        // Handle other payment methods for subscriptions if needed
      } catch (error) {
        // ... error handling
      }
    }
  };

  return (
    <div className="App">
      <section className="main-cont-comp">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 main-comp-sec">
              <h3>Add Money to Your Pot: Boost Your Savings with Contributions</h3>
              
              {/* Payment Type Toggle */}
              <div className="mb-4">
                <div className="btn-group w-100" role="group">
                  <button
                    type="button"
                    className={`btn ${paymentType === "one-time" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => handlePaymentTypeChange("one-time")}
                  >
                    One-Time Payment
                  </button>
                  <button
                    type="button"
                    className={`btn ${paymentType === "subscription" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => handlePaymentTypeChange("subscription")}
                  >
                    Subscription
                  </button>
                </div>
              </div>

              {paymentType === "one-time" ? (
                <form onSubmit={handleSubmit}>
                  <div className="col-12">
                    <div className="mr-1 row d-flex">
                      {/* Tannit Options */}
                      {[
                        { value: 120, price: "1200.00£" },
                        { value: 320, price: "3200.00£" },
                        { value: 1500, price: "15000.00£" },
                      ].map(({ value, price }) => (
                        <div
                          key={value}
                          className={`form-radio-check col ${
                            selectedTannits === value ? "selected" : ""
                          }`}
                          onClick={() => handleRadioChange(value)}
                          style={{
                            cursor: "pointer",
                            border:
                              selectedTannits === value
                                ? "2px solid #007bff"
                                : "1px solid #ccc",
                            padding: "10px",
                            borderRadius: "5px",
                          }}
                        >
                          <h4>
                            <span>
                              <img src="assets/icons/Tannit coin.svg" alt="" />
                            </span>
                            {value}
                          </h4>
                          <p>{price}</p>
                        </div>
                      ))}

                      {/* Custom Amount */}
                      <div className="col-12 not-sec mt-4">
                        <h5>Custom amount</h5>
                        <p>Amount of TANNIT desired</p>
                        <div className="input-group mb-3">
                          <div className="input-group-prepend">
                            <span className="input-group-text">
                              <img src="assets/icons/Tannit coin.svg" alt="" />
                            </span>
                          </div>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Number of TANNIT"
                            value={customTannits}
                            onChange={handleCustomChange}
                            min="1"
                          />
                          <div className="input-group-append">
                            <span className="input-group-text">
                              = {customTannits * 10 || 0}£
                            </span>
                          </div>
                        </div>
                        <label className="form-label note">
                          Note: 1 TANNIT = 10£
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Payment Methods and Submit */}
                  <PaymentMethodSection 
                    paymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                    reference={reference}
                    handleReferenceChange={handleReferenceChange}
                    isProcessing={isProcessing}
                    navigate={navigate}
                    handleSubmit={handleSubmit}
                  />
                </form>
              ) : (
                <div className="subscription-plans">
                  <div className="row row-cols-1 row-cols-md-3 g-4">
                    {subscriptionPlans.map((plan) => (
                      <div className="col" key={plan.id}>
                        <div className={`card h-100 pricing-card ${selectedPlan?.id === plan.id ? "border-primary border-2" : "border-light"} ${plan.popular ? "popular-plan" : ""}`}>
                          {plan.popular && (
                            <span className="badge bg-primary popular-badge">Popular</span>
                          )}
                          <div className="card-body p-4 justify-content-between d-flex flex-column">
                            <div>
                            <h5 className="card-title text-muted text-uppercase mb-3">{plan.name}</h5>
                            <h2 className="mb-3">
                              £{plan.price}<small className="text-muted fw-light">/{plan.interval}</small>
                            </h2>
                            <p className="text-muted mb-4">{plan.tannits} TANNITs per {plan.interval}</p>
                            <ul className="list-unstyled mb-4">
                              {plan.features.map((feature, index) => (
                                <li key={index} className="mb-2">
                                  <i className="bi bi-check-circle-fill text-primary me-2"></i>
                                  {feature}
                                </li>
                              ))}
                            </ul>
                            </div>
                            <button
                              type="button"
                              className={`btn ${selectedPlan?.id === plan.id ? "btn-primary" : "btn-outline-primary"} w-100`}
                              onClick={() => handlePlanSelect(plan)}
                            >
                              {selectedPlan?.id === plan.id ? "Selected" : "Select Plan"}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {selectedPlan && (
                    <form onSubmit={handleSubmit} className="mt-5">
                      <PaymentMethodSection 
                        paymentMethod={paymentMethod}
                        setPaymentMethod={setPaymentMethod}
                        reference={reference}
                        handleReferenceChange={handleReferenceChange}
                        isProcessing={isProcessing}
                        navigate={navigate}
                        handleSubmit={handleSubmit}
                      />
                    </form>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Extracted Payment Method Section for reusability
const PaymentMethodSection = ({
  paymentMethod,
  setPaymentMethod,
  reference,
  handleReferenceChange,
  isProcessing,
  navigate,
  handleSubmit
}) => (
  <>
    <div className="col-12 new-radio-2 mt-4">
      <h3>Payment method</h3>
      <div className="mr-1 row d-flex">
        {["Stripe", "Paypal", "GoCardless", "Investment Broker"].map((method) => (
          <div
            key={method}
            className={`form-radio-check2 col ${
              paymentMethod === method ? "selected" : ""
            }`}
            onClick={() => setPaymentMethod(method)}
            style={{
              cursor: "pointer",
              border:
                paymentMethod === method
                  ? "2px solid #007bff"
                  : "1px solid #ccc",
              padding: "10px",
              borderRadius: "5px",
            }}
          >
            <h4>
              <span>
                <img
                  src={`assets/icons/${method.toLowerCase()}.svg`}
                  alt=""
                />
              </span>
              {method}
            </h4>
          </div>
        ))}
      </div>
    </div>

    {paymentMethod === "Investment Broker" && (
      <div className="col-12 not-sec mt-4">
        <h5>Payment Reference</h5>
        <p>Enter your Investment Broker payment reference</p>
        <div className="input-group mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="Payment reference"
            value={reference}
            onChange={handleReferenceChange}
            required
          />
        </div>
      </div>
    )}

    <div className="verification-btns d-flex mt-4">
      <button
        type="button"
        className="btn radio-btn-1 btn-outline-secondary"
        onClick={() => navigate(-1)}
      >
        Cancel
      </button>
      <button
        type="submit"
        className="btn radio-btn-2 btn-primary"
        disabled={isProcessing}
      >
        {isProcessing ? "Processing..." : "Confirm Payment"}
      </button>
    </div>
  </>
);

export default AddMoney;